import React, { useState } from "react";
import { PlainButton, Form, SelectField, SubmitButton, Icon, classnames } from "@culturehq/components";

import { useProfile } from "app/context";
import { t, Trans } from "app/utils";

import ChqModal from "components/common/ChqModal";
import EventSlider from "../EventSlider";
import EventSelect from "../EventSelect";
import EventPortalButton from "../common/EventPortalButton";

import EventForm from "./EventForm";
import styles from "./EventForm.module.scss";

const getEventTypeTitle = (eventState, type) => {
  if (type && type.eventType) {
    return type.eventType;
  }
  if (eventState.eventType) {
    return eventState.eventType;
  }
  return "No Title";
};

const EventUpload = ({ eventState, type, handleResetEventType }) => {
  let image = (type && !eventState.image) ? type.image.originalUrl : eventState.image;

  // Only for update event
  if (eventState.image && eventState.image.originalUrl) {
    image = eventState.image.originalUrl;
  }

  return (
    <div className={classnames(styles["upload-img"], "upload-img")}>
      <div className={styles.imgHolder} style={{ backgroundImage: `url(${image})` }}>
        <div className={styles.coverImg}>
          <div className={styles.labelSec}>
            <span className={styles.labelTxt}>
              {getEventTypeTitle(eventState, type)}
            </span>
            <div className={classnames(styles["action-sec"], "action-sec")}>
              <PlainButton onClick={handleResetEventType} aria-label={t("Unselect category")}>
                <Icon icon="close" />
              </PlainButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const options = {
  public: [
    { value: true, label: t("Public - This event can be seen by everyone in your organization") },
    { value: false, label: t("Private - This event is only visible to those colleagues that are explicitly invited") }
  ],
  sponsored: [
    { value: false, label: t("No - This is not an official organization event") },
    { value: true, label: t("Yes - This is an official organization event") }
  ]
};

const EventType = ({
  eventState,
  handleEventState,
  handleImage,
  handleUnSaved,
  organizationValueOptions,
  onSubmit,
  selectedOrganizationValues,
  onSelectedOrgValues
}) => {
  const { profile } = useProfile();

  const [type, setType] = useState(null);
  const [message, setMessage] = useState("");

  const handleSubmit = async values => {
    await handleUnSaved(false);
    let processed = values;

    if (type || eventState.image) {
      const event_image = eventState.image || type.image.originalUrl;
      processed = { ...processed, ...type, image: event_image };

      if (eventState.id) {
        onSubmit(processed);
      } else {
        handleEventState(processed);
      }
    } else {
      setMessage(t("Please select event category"));
    }
  };

  const handleResetEventType = () => {
    setType(null);
    setMessage("");
    handleImage(null, null);
  };

  const onSetEventType = defaultVal => {
    handleUnSaved(true);
    const values = defaultVal.defaults();
    setType(values);
    setMessage("");
    handleImage(values.image.originalUrl, null);
  };

  const getInitialValues = {
    ...eventState,
    image: (type && !eventState.image) ? type.image.originalUrl : eventState.image
  };

  return (
    <EventForm icon="calendar-star" title={t("Which type of event would you like to create?")}>
      <p className={classnames(styles["m-b-35"], styles["f-s-18"])}>
        <Trans>
          Define the main attributes of your event. Select a category and
          relevant values to properly tag your event and provide context for
          users.
        </Trans>
      </p>
      <Form onSubmit={handleSubmit} initialValues={getInitialValues} id="event_type_form" className="event-form">
        <SelectField
          ariaLabel={t("Select event visibility")}
          className={classnames(styles["m-b-20"])}
          name="public"
          onChange={() => handleUnSaved(true)}
          options={options.public}
          required
        >
          <Trans>Private or Public<sup>*</sup></Trans>
        </SelectField>
        <div className={classnames(styles["m-b-20"])}>
          <label className={styles.customLabel}>
            <Trans>Event Category<sup>*</sup></Trans>
          </label>
          {(eventState.image) ? (
            <div className={classnames(styles["upload-wrapper"], "clearfix")}>
              <EventUpload
                eventState={eventState}
                type={type}
                handleResetEventType={handleResetEventType}
              />
            </div>
          )
            : (
              <div className={classnames(styles["categorie-wrapper"], "clearfix")}>
                <EventSlider onSetEventType={onSetEventType} />
                <ChqModal
                  className={styles.eventModal}
                  trigger={onTrigger => (
                    <span className={classnames(styles["link-txt"], "pull-right")} onClick={onTrigger}>
                      View all categories
                    </span>
                  )}
                >
                  <EventSelect onSetEventType={onSetEventType} />
                </ChqModal>
              </div>
            )}
          <p className={classnames(styles["m-t-5"], "chq-ffd--rq")}>{message}</p>
        </div>
        <SelectField
          ariaLabel={t("Select event organizational values")}
          className={classnames(styles["m-b-20"])}
          name="organizationValueIds"
          options={organizationValueOptions}
          value={selectedOrganizationValues}
          onChange={onSelectedOrgValues}
          placeholder={t("Choose any values that align to your event")}
          multiple
        >
          <Trans>Organizational Values</Trans>
        </SelectField>
        {profile.organizationAdmin && (
          <SelectField
            ariaLabel={t("Select if event is sponsored")}
            className={classnames(styles["m-b-30"], "drop-index")}
            name="sponsored"
            onChange={() => handleUnSaved(true)}
            options={options.sponsored}
            required
          >
            <Trans>Sponsored Event?</Trans>
          </SelectField>
        )}
        <EventPortalButton>
          {eventState.id
            ? (
              <SubmitButton primary form="event_type_form">
                {submitting => (submitting ? t("Saving...") : t("Save Event"))}
              </SubmitButton>
            )
            : (
              <SubmitButton primary form="event_type_form">
                {submitting => (submitting ? t("Go next...") : t("Next"))}
              </SubmitButton>
            )}
        </EventPortalButton>
      </Form>
    </EventForm>
  );
};

export default EventType;
